import type { HistoryActor, HistoryChange, HistoryItem, HistoryPage } from './changeLogTypes';

const isObject = (v: unknown): v is Record<string, unknown> =>
  !!v && typeof v === 'object' && !Array.isArray(v);

const isCount = (v: unknown): v is number => typeof v === 'number' && Number.isInteger(v) && v >= 0;

const stringOrNull = (v: unknown) => (typeof v === 'string' && v ? v : null);

const stringList = (v: unknown): string[] | undefined | null => {
  if (v === undefined) return undefined;
  if (!Array.isArray(v)) return null;
  return v.map((s) => String(s));
};

function parseActor(raw: unknown): HistoryActor | null {
  if (!isObject(raw)) return null;
  return { id: stringOrNull(raw.id), email: stringOrNull(raw.email), name: stringOrNull(raw.name) };
}

function parseChange(raw: unknown): HistoryChange | null {
  if (!isObject(raw) || typeof raw.field !== 'string') return null;
  const label = typeof raw.label === 'string' && raw.label ? raw.label : raw.field;
  // A secret never reaches the screen, even if a server sent its value by mistake.
  if (raw.redacted === true) return { field: raw.field, label, redacted: true };
  const added = stringList(raw.added);
  const removed = stringList(raw.removed);
  if (added === null || removed === null) return null;
  return {
    field: raw.field,
    label,
    before: raw.before,
    after: raw.after,
    ...(added ? { added } : {}),
    ...(removed ? { removed } : {}),
  };
}

function parseItem(raw: unknown): HistoryItem | null {
  if (!isObject(raw)) return null;
  const { id, at, source, action, summary, changes } = raw;
  if (typeof id !== 'string' || !id) return null;
  if (typeof at !== 'string' || Number.isNaN(Date.parse(at))) return null;
  if (source !== 'auth' && source !== 'platform') return null;
  if (!Array.isArray(changes)) return null;
  const parsedChanges: HistoryChange[] = [];
  for (const c of changes) {
    const change = parseChange(c);
    if (!change) return null;
    parsedChanges.push(change);
  }
  return {
    id,
    at,
    source,
    action: typeof action === 'string' ? action : '',
    summary: typeof summary === 'string' && summary ? summary : 'Changed',
    actor: parseActor(raw.actor),
    changes: parsedChanges,
  };
}

/** The history page, or null when the answer is not the documented shape. */
export function parseHistoryPage(raw: unknown): HistoryPage | null {
  if (!isObject(raw) || !Array.isArray(raw.items)) return null;
  if (!isCount(raw.total) || !isCount(raw.page) || !isCount(raw.limit)) return null;
  const items: HistoryItem[] = [];
  for (const i of raw.items) {
    const item = parseItem(i);
    if (!item) return null;
    items.push(item);
  }
  return { items, total: raw.total, page: raw.page, limit: raw.limit };
}
